import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Save, Lock, Package, ShieldAlert, Hourglass } from 'lucide-react';
import { useProductPricing, cleanInputs, EMPTY_INPUTS, termOverrideMissingReason, supportDiscountMissingReason } from './useProductPricing';
import { ProductPricingInputs } from './ProductPricingInputs';
import { ProductPricingResults } from './ProductPricingResults';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Product subscription calculator. With `estimateId` it loads/saves against the estimate; without it (wizard) it reports inputs via onChange. */
export function ProductPricingCalculator({ estimateId, initialInputs, onChange, suggested, entity = null, onSaved, reloadKey = 0 }) {
  const pp = useProductPricing(initialInputs || EMPTY_INPUTS, estimateId);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blended, setBlended] = useState(null);
  const [entities, setEntities] = useState([]);
  const [settingEntity, setSettingEntity] = useState(false);
  const lockEntity = entity || meta?.entity || null;
  const lockCcy = lockEntity?.default_currency_code || null;

  useEffect(() => {
    if (lockCcy && pp.inputs.currency !== lockCcy) pp.setInputs((p) => ({ ...p, currency: lockCcy }));
  }, [lockCcy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (meta?.entity_required) axios.get(`${API_URL}/api/settings/seertech-entities`, { withCredentials: true }).then((r) => setEntities(r.data?.entities || r.data || [])).catch(() => {});
  }, [meta?.entity_required]);

  const chooseEntity = async (eid) => {
    if (!eid) return;
    setSettingEntity(true);
    try {
      await axios.put(`${API_URL}/api/estimates/${estimateId}/selling-entity`, { contracting_entity_id: eid }, { withCredentials: true });
      const r = await axios.get(`${API_URL}/api/estimates/${estimateId}/product-pricing`, { withCredentials: true });
      setMeta(r.data);
      toast.success(`Selling Entity set — pricing in ${r.data.entity?.default_currency_code || 'its'} price book`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not set the Selling Entity'); }
    finally { setSettingEntity(false); }
  };

  useEffect(() => {
    if (!estimateId) return;
    axios.get(`${API_URL}/api/estimates/${estimateId}/product-pricing`, { withCredentials: true })
      .then((r) => {
        setMeta(r.data);
        if (r.data.inputs) pp.setInputs({ ...EMPTY_INPUTS, ...r.data.inputs, quantities: { ...EMPTY_INPUTS.quantities, ...r.data.inputs.quantities } });
        else if (r.data.suggested) {
          const s = r.data.suggested;
          pp.setInputs((p) => ({ ...p, currency: s.currency || null, start_date: (s.start_date || '').slice(0, 10), quantities: { ...p.quantities, active_users: s.active_users || '', private_cloud: !!s.private_cloud } }));
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load product pricing'));
  }, [estimateId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!estimateId && suggested) {
      pp.setInputs((p) => ({ ...p, currency: p.currency || suggested.currency || null, quantities: { ...p.quantities, active_users: p.quantities.active_users || suggested.active_users || '', private_cloud: p.quantities.private_cloud || !!suggested.private_cloud } }));
    }
  }, [suggested?.active_users, suggested?.private_cloud, suggested?.currency]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (onChange) onChange(pp.inputs, pp.preview); }, [pp.inputs, pp.preview]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!meta?.implementation || !pp.preview) return;
    const impl = meta.implementation;
    const fx = pp.preview.fx_to_aud || 1;
    const net = (pp.preview.totals.net_aud ?? pp.preview.totals.net) + (pp.preview.totals.services_net || 0) * fx;
    const cost = pp.preview.totals.cost_aud == null ? null : pp.preview.totals.cost_aud + (pp.preview.totals.services_cost || 0) * fx;
    const sell = (impl.sell_aud || 0) + net;
    const c = impl.cost_aud == null || cost == null ? null : impl.cost_aud + cost;
    setBlended({ tco_aud: sell, implementation_sell_aud: impl.sell_aud, product_sell_aud: net, cost_aud: c,
      margin_pct: c == null || sell <= 0 ? null : ((sell - c) / sell) * 100 });
  }, [meta, pp.preview]);

  const disabled = !!estimateId && (!meta || !meta.can_edit || meta.locked);
  const missingReason = Object.values(pp.inputs.discounts || {}).some((d) => Number(d?.pct) > 0 && !(d.reason || '').trim()) || termOverrideMissingReason(pp.inputs) || supportDiscountMissingReason(pp.inputs);

  const reload = () => axios.get(`${API_URL}/api/estimates/${estimateId}/product-pricing`, { withCredentials: true }).then((r) => setMeta(r.data)).catch(() => {});
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/estimates/${estimateId}/product-pricing`, cleanInputs(pp.inputs), { withCredentials: true });
      if (r.data.requires_approval) { toast.warning(r.data.message); await reload(); return; }
      toast.success(`Product pricing saved — v${r.data.version_number} (${r.data.results?.currency})`);
      setMeta((m) => ({ ...m, saved_results: r.data.results, saved_at: new Date().toISOString(), stale_vs_saved: false }));
      if (onSaved) onSaved(r.data.results);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save product pricing'); } finally { setSaving(false); }
  };
  const decide = async (approve) => {
    const note = window.prompt(`${approve ? 'Approve' : 'Decline'} this below-guardrail subscription — note (optional)`) ?? null;
    if (note === null) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/product-pricing/approvals/${meta.pending_approval.id}/${approve ? 'approve' : 'decline'}`, { note }, { withCredentials: true });
      toast.success(approve ? 'Approved — pricing saved to the estimate' : 'Declined — requester notified'); await reload();
      if (approve) window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record decision'); } finally { setSaving(false); }
  };
  const pa = meta?.pending_approval;

  const dirty = meta?.saved_results && pp.preview && Math.abs((meta.saved_results.totals?.net || 0) - pp.preview.totals.net) > 0.5;
  const g = pp.preview?.guardrail;
  const breached = !!g?.breached;

  return (
    <div className="space-y-5" data-testid="product-pricing-calculator">
      {estimateId && meta?.entity_required && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-2" data-testid="pp-entity-required">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900"><ShieldAlert className="w-4 h-4" />Choose the Selling Entity first</div>
          <p className="text-xs text-amber-800">The Selling Entity sets the price book (currency) used for this subscription. Pricing can't be saved until it is set.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select defaultValue="" onChange={(e) => chooseEntity(e.target.value)} disabled={settingEntity || disabled} className="h-8 text-xs border border-amber-300 rounded-md px-2 bg-white text-[#0F2040] min-w-[260px]" data-testid="pp-entity-select">
              <option value="">Select the Selling Entity…</option>
              {entities.map((en) => <option key={en.id} value={en.id}>{en.code || en.id}{en.default_currency_code ? ` (${en.default_currency_code})` : ''} — {en.name}</option>)}
            </select>
            {settingEntity && <Loader2 className="w-4 h-4 animate-spin text-amber-700" />}
          </div>
        </div>
      )}
      {!estimateId && !entity && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2" data-testid="pp-entity-required-wizard"><ShieldAlert className="w-4 h-4" />Choose the Selling Entity on the Conditions step — the price book follows it.</div>
      )}
      <fieldset disabled={estimateId ? !!meta?.entity_required : !entity} className={(estimateId ? meta?.entity_required : !entity) ? 'opacity-50 pointer-events-none space-y-5' : 'space-y-5'} data-testid="pp-body">
      {estimateId && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <Package className="w-3.5 h-3.5 text-slate-400" />
            {meta?.saved_at ? <span data-testid="pp-saved-info">Saved {new Date(meta.saved_at).toLocaleString()} by {meta.saved_by}{meta.stale_vs_saved && <span className="ml-1 text-amber-700">· price book changed since — re-save to update</span>}</span> : <span data-testid="pp-unsaved-info">Not yet saved on this estimate</span>}
            {meta?.locked && <span className="inline-flex items-center gap-1 text-amber-700" data-testid="pp-locked"><Lock className="w-3 h-3" /> locked</span>}
          </div>
          <Button size="sm" onClick={save} disabled={disabled || saving || !pp.preview || missingReason} className="h-8 bg-[#0F2040] hover:bg-[#1a3260] text-white" data-testid="pp-save-button">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}{breached ? 'Request leadership approval' : dirty ? 'Save changes' : 'Save to estimate'}
          </Button>
        </div>
      )}
      {pa && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2 flex-wrap" data-testid="pp-pending-approval">
          <Hourglass className="w-3.5 h-3.5 shrink-0" />
          <span><b>Awaiting leadership approval</b> — {pa.requested_by_name} requested a subscription at combined margin {pa.combined_margin_pct}% (guardrail {pa.min_margin_pct}%, discretionary discounts A${Math.round(pa.discounts_aud || 0).toLocaleString()}) on {new Date(pa.created_at).toLocaleString()}. The saved pricing stays in force until a different leader decides.</span>
          {meta.is_leader && pa.requested_by !== meta.user_id && (
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-emerald-700" disabled={saving} onClick={() => decide(true)} data-testid="pp-approval-approve">Approve & save</Button>
              <Button size="sm" variant="outline" className="h-7 text-rose-600" disabled={saving} onClick={() => decide(false)} data-testid="pp-approval-decline">Decline</Button>
            </span>
          )}
        </div>
      )}
      {meta?.approved_by && !pa && <p className="text-[10px] text-slate-500" data-testid="pp-approved-by">Saved pricing was below guardrail and approved by {meta.approved_by}.</p>}
      {g?.checked && (
        <div className={`rounded-md border px-3 py-2 text-xs ${breached ? 'border-rose-300 bg-rose-50 text-rose-800' : g.headroom_pts < 5 ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`} data-testid="pp-guardrail">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
            <span className="font-semibold" data-testid="pp-guardrail-headline">
              {breached ? `Combined margin ${g.combined_margin_pct}% is below the ${g.min_margin_pct}% guardrail` : `Combined margin ${g.combined_margin_pct}% · ${g.headroom_pts} pts above the ${g.min_margin_pct}% guardrail`}
            </span>
            <span className="opacity-80">{g.includes_implementation ? 'implementation + product' : 'product only'}{g.discounts_aud > 0 ? ` · discretionary discounts A$${Math.round(g.discounts_aud).toLocaleString()} (undiscounted ${g.undiscounted_margin_pct}%)` : ''}</span>
          </div>
          {breached && g.lines.length > 0 && (
            <ul className="mt-1 ml-5 list-disc space-y-0.5" data-testid="pp-guardrail-lines">
              {g.lines.map((l) => (
                <li key={l.key} data-testid={`pp-guardrail-line-${l.key}`}>
                  <span className="font-medium">{l.label}</span> discount (A${Math.round(l.discount_aud).toLocaleString()}) {l.flag === 'pushes_below' ? `pushes the deal below guardrail — without it the margin would be ${l.margin_without_pct}%` : `contributes — margin without it would still be ${l.margin_without_pct}%`}
                </li>
              ))}
            </ul>
          )}
          {breached && !g.caused_by_discounts && <div className="mt-1 ml-5">The deal is below guardrail even before discretionary discounts — review pricing or the implementation estimate.</div>}
        </div>
      )}
      {missingReason && <p className="text-[11px] text-rose-600" data-testid="pp-missing-reason">Every discretionary discount (including on support services, and the term-discount override) needs a reason before saving.</p>}
      <ProductPricingInputs book={pp.book} currencies={pp.currencies} components={pp.components} inputs={pp.inputs} setField={pp.setField} setQty={pp.setQty} entityLock={lockEntity}
        setDiscount={pp.setDiscount} setCustom={pp.setCustom} preview={pp.preview} disabled={disabled} flaggedLines={breached ? Object.fromEntries(g.lines.map((l) => [l.key, l.flag])) : {}} />
      {pp.previewError && <p className="text-xs text-rose-600" data-testid="pp-preview-error">{pp.previewError}</p>}
      <ProductPricingResults preview={pp.preview} blended={estimateId ? blended : null} implementation={meta?.implementation} loading={pp.loading} />
      </fieldset>
    </div>
  );
}
